// The WebGL2 backend: the primitives of `gfx/types.ts`, built over the
// context of this folder.
//
// It is the whole of what the engine needs to know about WebGL2. The engine
// asks `createGlBackend` for one of these and then speaks to interfaces
// only, so the WebGPU backend drops into the same slot.

import { createGL } from './context';
import { Program } from './program';
import { InstanceBuffer } from './instances';
import { MeshBuffer } from './mesh';
import { DataTexture, AtlasTexture } from './texture';
import type { QueryKnobs } from '../knobs';
import {
  FALLBACK_MAX_TEXTURE,
  type AttribSpec, type DataFormat, type GfxBackend,
} from '../gfx/types';
import type { ProgramSource } from '../shaders/program';

/**
 * Claims the canvas for WebGL2 and hands back the primitives.
 *
 * It THROWS where the browser gives no context. That stop is the one stop
 * of the client: WebGL2 is the floor and nothing lies below it.
 */
export function createGlBackend(
  canvas: HTMLCanvasElement, knobs?: QueryKnobs,
): GfxBackend {
  const glc = createGL(canvas, knobs);
  return {
    gfx: glc,
    pipeline(src: ProgramSource) {
      return new Program(glc, src.name, src.glsl.vertex, src.glsl.fragment);
    },
    instances(floatsPerInstance: number, attribs: readonly AttribSpec[], capacity: number) {
      return new InstanceBuffer(glc, floatsPerInstance, attribs, capacity);
    },
    mesh(floatsPerVertex: number, attribs: readonly AttribSpec[]) {
      return new MeshBuffer(glc, floatsPerVertex, attribs);
    },
    dataTexture(
      name: string, width: number, height: number, format: DataFormat, linear?: boolean,
    ) {
      return new DataTexture(glc, name, width, height, format, linear);
    },
    atlasTexture(source: HTMLCanvasElement) {
      return new AtlasTexture(glc, source);
    },
    maxTextureSize() {
      return Number(glc.gl.getParameter(glc.gl.MAX_TEXTURE_SIZE)) || FALLBACK_MAX_TEXTURE;
    },
  };
}
